/**
 * NotificationBell — Cloche de notifications dans FloatingNav
 * Polling 60s, panneau deroulant glassmorphism
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Badge, Popover, Chip, Button } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/colors';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SEVERITY_STYLES = {
  critical: { label: 'Critique', color: colors.bordeaux, bg: 'rgba(139,26,26,0.08)' },
  warning: { label: 'Vigilance', color: colors.gold, bg: 'rgba(196,146,10,0.08)' },
  info: { label: 'Info', color: colors.primary, bg: 'rgba(27,94,64,0.08)' },
};

function NotificationBell({ brandId }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!brandId) return;
    try {
      const resp = await fetch(`${API_URL}/api/alerts/unread-count?brand_id=${brandId}`, {
        headers: authHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) { /* silent */ }
  }, [brandId, authHeaders]);

  const fetchNotifications = useCallback(async () => {
    if (!brandId) return;
    try {
      const resp = await fetch(`${API_URL}/api/alerts/?brand_id=${brandId}&limit=20`, {
        headers: authHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        setNotifications(data.notifications || []);
      }
    } catch (e) { /* silent */ }
  }, [brandId, authHeaders]);

  // Poll unread count every 60s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const handleMarkRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/alerts/${id}/read`, {
        method: 'PUT',
        headers: authHeaders(),
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/alerts/read-all?brand_id=${brandId}`, {
        method: 'PUT',
        headers: authHeaders(),
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const handleNotifClick = (notif) => {
    handleMarkRead(notif.id);
    if (notif.type === 'crisis' && notif.data?.crisis_id) {
      navigate('/crisis');
    }
    handleClose();
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return "a l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return `il y a ${Math.floor(diff / 86400)}j`;
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="Notifications"
        onClick={handleOpen}
        sx={{
          color: unreadCount > 0 ? colors.bordeaux : colors.textLight,
          '&:hover': { color: colors.primary },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            borderRadius: '16px',
            bgcolor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${colors.borderLight}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.borderLight}`,
        }}>
          <Typography sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.85rem', fontWeight: 600, color: colors.charcoal,
          }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllRead}
              sx={{
                fontSize: '0.72rem', fontFamily: '"DM Sans", sans-serif',
                color: colors.primary, textTransform: 'none',
              }}
            >
              Tout marquer comme lu
            </Button>
          )}
        </Box>

        {/* List */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{
                fontSize: '0.82rem', color: colors.textLight,
                fontFamily: '"DM Sans", sans-serif',
              }}>
                Aucune notification
              </Typography>
            </Box>
          ) : (
            notifications.map((notif, idx) => {
              const style = SEVERITY_STYLES[notif.severity] || SEVERITY_STYLES.info;
              return (
                <Box
                  key={notif.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotifClick(notif)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotifClick(notif); } }}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer',
                    bgcolor: notif.read ? 'transparent' : 'rgba(27,94,64,0.03)',
                    borderBottom: idx < notifications.length - 1
                      ? `1px solid ${colors.borderLight}` : 'none',
                    '&:hover': { bgcolor: 'rgba(27,94,64,0.06)' },
                    transition: 'background 0.2s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={style.label}
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.65rem',
                        fontFamily: '"DM Sans", sans-serif', fontWeight: 600,
                        bgcolor: style.bg, color: style.color,
                        border: `1px solid ${style.color}30`,
                      }}
                    />
                    <Typography sx={{
                      fontSize: '0.68rem', color: colors.textLight,
                      fontFamily: '"DM Sans", sans-serif', ml: 'auto',
                    }}>
                      {timeAgo(notif.created_at)}
                    </Typography>
                  </Box>
                  <Typography sx={{
                    fontSize: '0.78rem', fontFamily: '"DM Sans", sans-serif',
                    color: colors.charcoal,
                    fontWeight: notif.read ? 400 : 600,
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {notif.title}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
}

export default NotificationBell;
